import { Grid } from '@interstate/components/Grid';
import { Typography } from '@interstate/components/Typography';
import type { LineItemWithChildrenProps } from '../../types/LineItemTypes';
import { getTestId } from '../utils/htmlUtils';
import { useDealSummary } from '../../common/DealSummaryProvider';
import { toRem } from '../../utils/pixelUtils';

const LineItemWithChildren = ({
    label,
    value,
    childItems,
    containerStyles,
    testId,
    labelStyles,
    valueStyles
}: LineItemWithChildrenProps) => {
    const testIdStr = getTestId({ label, testId });
    const labelId = `${testIdStr}-label`;
    const valueId = `${testIdStr}-amount`;

    const { showDetailedView } = useDealSummary();

    return (
        <>
            <Grid
                container
                sx={{
                    justifyContent: 'space-between',
                    width: '100%',
                    ...containerStyles,
                    ...(showDetailedView ? { height: 'fit-content' } : {})
                }}
                data-testid={testId}
            >
                <Typography
                    tag="span"
                    variant="label-sm"
                    color="sem.color.on-surface.muted"
                    data-testid={labelId}
                    sx={{
                        flex: '1 1 0',
                        minWidth: 0,
                        maxWidth: 'calc(100% - 55px)',
                        marginRight: toRem(8),
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        ...labelStyles
                    }}
                >
                    {label}
                </Typography>
                <Typography
                    tag="span"
                    variant="body-xs"
                    color="sem.color.on-surface.default"
                    data-testid={valueId}
                    sx={{
                        flex: '0 0 auto',
                        whiteSpace: 'nowrap',
                        ...valueStyles
                    }}
                >
                    {value}
                </Typography>
            </Grid>
            {childItems?.map(({ label: childLabel, value: childValue }, idx) => (
                <Grid
                    container
                    justifyContent="space-between"
                    ml="16px"
                    width="calc(100% - 16px)"
                    key={`${childLabel}-${childValue}`}
                    data-testid={`${labelId}-${idx}`}
                    flexWrap="nowrap"
                    alignItems="flex-start"
                >
                    <Typography
                        tag="span"
                        variant="label-sm"
                        color="sem.color.on-surface.muted"
                        sx={{
                            flexGrow: '1',
                            minWidth: '0',
                            maxWidth: 'calc(100% - 55px)',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            pr: 1
                        }}
                    >
                        {childLabel}
                    </Typography>
                    <Typography
                        tag="span"
                        variant="body-xs"
                        color="sem.color.on-surface.default"
                        sx={{ flexShrink: '0' }}
                        data-testid={`${valueId}-${idx}`}
                    >
                        {childValue}
                    </Typography>
                </Grid>
            ))}
        </>
    );
};

export default LineItemWithChildren;
